import { ref } from 'vue';

import {
  capitalize,
} from '@/utils/stringHelpers';

import getPropertyData from '@/utils/getPropertyData';
import opportunitiesRequestHandler from '@/utils/opportunitiesRequestHandler';
import formatValue from '@/utils/formatValue';
import bdLogger from '@/utils/bdLogger';
import countriesData from '@/utils/countriesData';

import useMainState from '@/state/useMainState';

import {
  percentFormat,
  numericCurrFormat,
} from '@/config/valueFormatConfig';
import i18n from '@/config/i18n';

const {
  propertyId,
  isOverview,
} = useMainState();

const { t } = i18n.global;

const opportunities = ref([]);
const userOpportunities = ref([]);
const meta = ref({});
const currentProperty = ref(null);
const propertyPerformsOk = ref(false);
const resolved = ref(false);
const polling = ref(false);

const fetchOpportunities = async () => {
  if ((currentProperty.value !== propertyId.value) && !polling.value) {
    propertyPerformsOk.value = false;
    resolved.value = false;
    polling.value = true;
    opportunities.value = [];
    currentProperty.value = propertyId.value;
    try {
      const { data } = await opportunitiesRequestHandler('GET', `/opportunities/${propertyId.value}`);
      propertyPerformsOk.value = data.data.some((opp) => opp.status === 1);
      opportunities.value = data.data
        .filter((opp) => opp.status === 0)
        .map((opp) => ({
          ...opp,
          isDisparity: opp.goal === 'disparities',
          title: `${opp.goal}.action`,
          description: `${opp.goal}.${opp.action} ${opp.metric}`,
          uplift: formatValue(opp.uplift, percentFormat),
          impactLabel: t(`${capitalize(opp.impact === 'max' ? 'High' : opp.impact)}`),
          rawMetric: opp.metric,
          metric: t(opp.metric.toLowerCase()),
          strataType: opp.strataType.replace('_', ' '),
          rawStrataValue: opp.strataValue,
          strataValue: opp.strataType === 'country'
            ? t(countriesData
              .filter((country) => country.country_code === opp.strataValue)[0].country)
            : opp.strataValue,
          lostRevenue: opp.goal === 'disparities' ? opp.lostRevenue : formatValue(opp.lostRevenue, numericCurrFormat),
        }));
      meta.value = data.meta;
      resolved.value = 'property';
      polling.value = false;
    } catch (error) {
      resolved.value = true;
      polling.value = false;
      bdLogger.warn(error);
    }
  }
};

const fetchAllOpportunities = async () => {
  if (!polling.value && !userOpportunities.value.data) {
    resolved.value = false;
    polling.value = true;
    userOpportunities.value = [];
    try {
      const { data } = await opportunitiesRequestHandler(
        'GET',
        '/opportunities',
        undefined,
        { useCache: true },
      );

      userOpportunities.value = data;

      userOpportunities
        .value.data.disparities.properties = userOpportunities
          .value.data.disparities.properties
          .filter((opp) => opp.status === 0)
          .map((opp) => ({
            ...opp,
            name: getPropertyData(opp.propertyId).name,
            impactLabel: t(`${capitalize(opp.impact === 'max' ? 'High' : opp.impact)}`),
          }));

      userOpportunities
        .value.data.revenue.properties = userOpportunities
          .value.data.revenue.properties
          .filter((opp) => opp.status === 0)
          .map((opp) => ({
            ...opp,
            name: getPropertyData(opp.propertyId).name,
            impactLabel: t(`${capitalize(opp.impact === 'max' ? 'High' : opp.impact)}`),
            lostRevenue: formatValue(opp.lostRevenue, numericCurrFormat),
          }));

      currentProperty.value = 0;
      resolved.value = 'all';
      polling.value = false;
    } catch (error) {
      resolved.value = true;
      polling.value = false;
      bdLogger.warn(error);
    }
  }
};

const filteredOpportunities = (maxItems) => {
  if (resolved.value === 'all') { return []; }
  const entries = isOverview.value
    ? opportunities.value : opportunities.value
      .filter((opp) => opp.propertyId === propertyId.value && opp.status === 0);
  return maxItems ? entries.slice(0, maxItems) : entries;
};

const groupedOpportunities = () => {
  const grouped = {};
  if (resolved.value === 'all') { return { disparities: [], revenue: [] }; }
  const entries = opportunities.value.filter(
    (opp) => opp.propertyId === propertyId.value && opp.status === 0,
  );
  grouped.disparities = entries.filter((opp) => opp.goal === 'disparities');
  grouped.revenue = entries.filter((opp) => opp.goal === 'revenue');
  return grouped;
};

export default function useOpportunitiesState() {
  return {
    fetchOpportunities,
    fetchAllOpportunities,
    opportunities,
    userOpportunities,
    filteredOpportunities,
    groupedOpportunities,
    propertyPerformsOk,
    resolved,
    currentProperty,
    meta,
  };
}
